import { Box, BoxStatus, Flexbox, Heading, CodeIcon, Body } from "codekit";
import { useGetDataGraphicFacturasSinDoQuery } from "../../../../libs/redux/slices/Graficsx/Invsindo/invsindoApiSlice";
import { useGetUrlRedirectCimaMutation } from "../../../../libs/redux/slices/Graficsx/graficsxApiSlice";

const Invsindo = ({ graphName }) => {
  const { data, isLoading, isError } = useGetDataGraphicFacturasSinDoQuery();
  const [getUrlRedirectCima] = useGetUrlRedirectCimaMutation();

  /**
   * Maneja el clic en las tarjetas para redirigir al tracking de facturas.
   * Calcula los rangos de fechas dinámicamente.
   */
  const handleRedirect = async (rangeKey) => {
    const today = new Date().toISOString().split('T')[0];
    const getDateAgo = (days) => {
      const d = new Date();
      d.setDate(d.getDate() - days);
      return d.toISOString().split('T')[0];
    };

    let filters = {};

    // Configuración de filtros según el rango seleccionado (30-60, 61-89, 90+)
    switch (rangeKey) {
      case 'range1': // 30-60 días
        filters = { [`filters[0][DSVCYYYY.REGFECXX][BETWEEN]`]: `${getDateAgo(60)},${getDateAgo(30)}` };
        break;
      case 'range2': // 61-89 días
        filters = { [`filters[0][DSVCYYYY.REGFECXX][BETWEEN]`]: `${getDateAgo(89)},${getDateAgo(61)}` };
        break;
      case 'range3': // 90+ días
        filters = { [`filters[0][DSVCYYYY.REGFECXX][LTE]`]: getDateAgo(90) };
        break;
      default:
        break;
    }


    const payload = {
      MODIDXXX: "114", // Módulo Faccomxx
      PROIDXXX: "100", // Proceso Salfactx
      BANEJEFU: "SI",
      NOMFUNXX: "handleToggleFilter",
      SELECTOR: {},
      ...filters
    };

    await getUrlRedirectCima(payload)
      .unwrap()
      .then((response) => {
        if (response.data) {
          // Si la respuesta es una URL completa de API, extraemos el token o redirigimos a /external/
          const token = response.data.split('/').pop();
          const externalUrl = `${window.location.origin}/external/${token}`;
          window.open(externalUrl, "_blank");
        }
      })
      .catch((err) => {
        console.error("Error al redirigir:", err);
      });
  };

  if (isLoading) return <BoxStatus variant="loading" text="Cargando información del dashboard..." />;
  if (isError) return <BoxStatus variant="error" text="Error al cargar la información del dashboard" />;

  const dashboardData = data?.data || {};
  const totalCount = dashboardData.total || 0;
  const ranges = dashboardData.ranges || {};

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const currentDateFormatted = new Intl.DateTimeFormat('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }).format(new Date());

  return (
    <Box bg="neutral-100" rounded="lg" style={{ width: 'calc(100% - 48px)', maxWidth: '1230px', display: 'flex', flexDirection: 'column', margin: '24px 0 24px 24px', height: 'auto', boxSizing: 'border-box', alignSelf: 'start', overflowX: 'hidden', border: '1px solid #e0e0e0', padding: '24px', backgroundColor: '#f5f5f5' }}>
      {/* Header Section - Zero margin */}
      <Box mb={2}>
        <Flexbox justifyContent="between" alignItems="center">
          <Box>
            <Heading variant="heading-4" color="neutral-900" weight="bold" uppercase mb={0} style={{ fontSize: '18px' }}>{graphName || "FACTURAS ACTIVAS SIN DO ASOCIADO"}</Heading>
            <Body variant="body-s" color="neutral-500" style={{ fontSize: '12px' }}>{capitalize(currentDateFormatted)}</Body>
          </Box>
        </Flexbox>
      </Box>

      {/* Summary Section - Force Baseline */}
      <Box mb={4}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '110px', fontWeight: '100', lineHeight: '1', letterSpacing: '-5px', color: '#111827', fontFamily: 'sans-serif' }}>{totalCount}</span>
          <Box style={{ paddingBottom: '16px' }}>
            <Body variant="body-m" color="neutral-500" style={{ lineHeight: '1', fontWeight: '400', fontSize: '16px' }}>Facturas Activas</Body>
            <Body variant="body-s" color="neutral-500" style={{ lineHeight: '1', fontSize: '16px' }}>sin DO por asociar</Body>
          </Box>
        </div>
      </Box>

      {/* Percentage Bar Section - High Contrast and Visible */}
      <Box mb={8}>
        <div style={{ height: '16px', width: '100%', display: 'flex', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#e5e7eb' }}>
          {Object.entries(ranges).map(([key, range]) => {
            const getColor = (color) => {
              if (color === 'success') return '#16a34a';
              if (color === 'warning') return '#d97706';
              if (color === 'error') return '#dc2626';
              return '#9ca3af';
            };
            return (
              <div
                key={key}
                style={{
                  width: `${range.percentage}%`,
                  height: '100%',
                  backgroundColor: getColor(range.color),
                  transition: 'width 0.5s ease'
                }}
              />
            );
          })}
        </div>
      </Box>

      <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', width: '100%' }}>
        {Object.entries(ranges).map(([key, range]) => (
          <Box spacing={4}
            key={key}
            bg={`${range.color}-100`}
            rounded="xl"
            style={{
              flex: '1 1 0px',
              minWidth: '280px',
              display: 'flex',
              flexDirection: 'column',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
            }}
            p={8}
            onClick={() => handleRedirect(key)}
          >
            <Body variant="body-xs" color={`${range.color}-800`} weight="bold" uppercase mb={4}>
              {range.label}
            </Body>

            <span style={{
              fontSize: '64px',
              fontWeight: '100',
              lineHeight: '1',
              color: range.color === 'success' ? '#166534' : range.color === 'warning' ? '#9a3412' : '#991b1b'
            }}>
              {range.count}
            </span>

            <Body variant="body-m" color={`${range.color}-800`} mb={8} style={{ fontSize: '16px' }}>
              {range.days} días
            </Body>

            {/* Inner Progress Bar - Saturated Flow */}
            <Box mb={5}>
              <Box
                width="100%"
                height={6}
                bg={`${range.color}-200`}
                rounded="full"
                style={{ overflow: 'hidden' }}
              >
                <Box
                  width={`${range.percentage}%`}
                  height="100%"
                  bg={`${range.color}-700`}
                  rounded="full"
                />
              </Box>
            </Box>

            {/* Percentage Badge */}
            <Flexbox justifyContent="between" alignItems="center">
              <Box
                px={3}
                py={1}
                rounded="xs"
                bg={`${range.color}-200`}
              >
                <Body variant="body-s" color={`${range.color}-800`} weight="bold">
                  {range.percentage}%
                </Body>
              </Box>
              <Box color={`${range.color}-700`}>
                <CodeIcon variant="arrow-right" width={14} />
              </Box>
            </Flexbox>
          </Box>
        ))}
      </div>
    </Box>
  );
};


export default Invsindo;
